import React from "react";
import { REGISTER_URL, WHATSAPP_URL, COHORT_INFO } from "@/lib/site";
import {
    ArrowRight, MessageCircle, ArrowDown, Sparkles, ShieldCheck, CalendarDays,
    Lightbulb, FileText, ClipboardList, Layers, PenLine, BookOpen,
} from "lucide-react";

const BADGES = [
    COHORT_INFO.registrationStatus,
    `${COHORT_INFO.cohortLabel}: ${COHORT_INFO.cohortMonth}`,
    COHORT_INFO.mode,
    "Beginner Friendly",
    "Certificate Provided",
    "Limited Seats",
];

const VALUE_CARDS = [
    "Research proposal clarity",
    "Interview protocol template",
    "Coding worksheet",
    "Findings writing framework",
    "Publication readiness checklist",
];

const STAGES = [
    { label: "Idea", Icon: Lightbulb },
    { label: "Proposal", Icon: FileText },
    { label: "Data", Icon: ClipboardList },
    { label: "Analysis", Icon: Layers },
    { label: "Findings", Icon: PenLine },
    { label: "Publication", Icon: BookOpen },
];

function JourneyIllustration() {
    return (
        <div
            className="relative rounded-[28px] p-6 sm:p-8 overflow-hidden"
            data-testid="hero-journey-illustration"
            style={{
                background:
                    "linear-gradient(160deg, #0f1e42 0%, #1c2e5e 55%, #7a1f2f 100%)",
            }}
        >
            {/* Ornamental rings */}
            <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full border border-[color:var(--gold)]/25" />
            <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full border border-[color:var(--gold)]/15" />
            <div className="absolute inset-3 rounded-[22px] border border-white/10 pointer-events-none" />

            <div className="relative z-10">
                <div className="flex items-center justify-between">
                    <div className="font-mono-label text-[color:var(--gold-soft)]">
                        Research Pathway
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">
                        6 stages · 1 clear path
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4">
                    {STAGES.map((s, i) => {
                        const Icon = s.Icon;
                        return (
                            <div
                                key={s.label}
                                className="relative rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-sm p-4 flex flex-col items-center text-center hover:border-[color:var(--gold-soft)]/50 transition-colors"
                                style={{ animation: `float-y 4s ease-in-out ${i * 0.4}s infinite` }}
                            >
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[color:var(--gold)]/15 border border-[color:var(--gold)]/30 text-[color:var(--gold-soft)]">
                                    <Icon className="h-4 w-4" />
                                </span>
                                <div className="mt-3 font-serif text-lg text-white leading-tight">
                                    {s.label}
                                </div>
                                <div className="mt-1 text-[10px] font-mono-label text-[color:var(--gold-soft)]">
                                    Stage {String(i + 1).padStart(2, "0")}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-4">
                    <div className="flex-1">
                        <div className="font-mono-label text-[color:var(--gold-soft)]">
                            Materials you leave with
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {[
                                "Proposal checklist",
                                "Interview protocol",
                                "Coding worksheet",
                                "Theme map",
                                "Findings guide",
                                "Journal draft",
                            ].map((m) => (
                                <span
                                    key={m}
                                    className="rounded-full bg-white/10 border border-white/10 px-3 py-1 text-[11px] text-white/80"
                                >
                                    {m}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden grain-overlay bg-[color:var(--paper-tint)]"
        >
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 10% 10%, rgba(181,138,43,0.12), transparent 42%), radial-gradient(circle at 92% 88%, rgba(122,31,47,0.10), transparent 45%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                {/* Left copy */}
                <div className="lg:col-span-7 reveal-up">
                    <div
                        data-testid="hero-label"
                        className="inline-flex items-center gap-2 rounded-full border border-[color:var(--gold)]/40 bg-white px-3.5 py-1.5 font-mono-label text-[color:var(--maroon)]"
                    >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--maroon)] pulse-dot" />
                        Live Online Research Clinic
                    </div>

                    <h1
                        data-testid="hero-title"
                        className="mt-6 font-serif tracking-tight text-[color:var(--navy)] text-5xl sm:text-6xl lg:text-[72px] leading-[1.03]"
                        style={{ fontWeight: 500 }}
                    >
                        From Confused to Confident
                        <span className="block italic text-[color:var(--maroon)]" style={{ fontWeight: 400 }}>
                            in Qualitative Research
                        </span>
                    </h1>

                    <p
                        data-testid="hero-subtitle"
                        className="mt-6 text-lg sm:text-xl text-slate-700 max-w-2xl leading-relaxed"
                    >
                        A practical live online workshop series that helps you move from a vague
                        research idea to a clear proposal, better interview preparation, meaningful
                        analysis, strong findings and publication-ready writing.
                    </p>

                    {/* Value cards */}
                    <div className="mt-8" data-testid="hero-value-cards">
                        <div className="font-mono-label text-slate-500 mb-3">
                            What you leave with
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                            {VALUE_CARDS.map((v, i) => (
                                <div
                                    key={v}
                                    data-testid={`hero-value-card-${i + 1}`}
                                    className="flex items-center gap-2.5 rounded-xl bg-white border border-[color:var(--line)] px-3.5 py-2.5"
                                >
                                    <Sparkles className="h-3.5 w-3.5 text-[color:var(--gold)] shrink-0" />
                                    <span className="text-sm text-[color:var(--navy)] font-medium">
                                        {v}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Badges */}
                    <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-badges">
                        {BADGES.map((b) => (
                            <span
                                key={b}
                                data-testid={`badge-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--line)] bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700"
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--maroon)]" />
                                {b}
                            </span>
                        ))}
                    </div>

                    {/* Key details card */}
                    <div
                        data-testid="hero-key-details"
                        className="mt-8 rounded-2xl bg-white border border-[color:var(--line)] p-5 sm:p-6 max-w-xl"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="font-mono-label text-[color:var(--maroon)]">
                                    {COHORT_INFO.cohortType} · {COHORT_INFO.cohortLabel} · {COHORT_INFO.cohortMonth}
                                </div>
                                <div className="mt-2 font-serif text-2xl text-[color:var(--navy)] leading-tight">
                                    {COHORT_INFO.frequency} in {COHORT_INFO.cohortMonth}
                                </div>
                                <div className="text-sm text-slate-600 mt-1">
                                    {COHORT_INFO.time} · {COHORT_INFO.timezone} · {COHORT_INFO.mode}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="font-mono-label text-slate-500">
                                    Full Series
                                </div>
                                <div className="mt-1 font-serif text-3xl text-[color:var(--maroon)]">
                                    RM 399
                                </div>
                            </div>
                        </div>
                        <div className="hr-gold mt-5" />
                        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                            <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--gold)]" />
                            Certificate of attendance · Live sessions · Practical templates
                        </div>
                    </div>

                    {/* CTAs */}
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <a
                            href={REGISTER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-register-btn"
                            className="btn-maroon group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm tracking-wide"
                        >
                            Register Now
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </a>
                        <a
                            href="#journey"
                            data-testid="hero-journey-btn"
                            className="btn-outline-navy inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm tracking-wide"
                        >
                            <ArrowDown className="h-4 w-4" />
                            View Workshop Journey
                        </a>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-btn"
                            className="btn-ghost inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm tracking-wide"
                        >
                            <MessageCircle className="h-4 w-4" />
                            WhatsApp for Details
                        </a>
                    </div>
                </div>

                {/* Right visual */}
                <div className="lg:col-span-5 reveal-up" style={{ animationDelay: "0.1s" }}>
                    <JourneyIllustration />
                    <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 justify-center lg:justify-start">
                        <CalendarDays className="h-3.5 w-3.5 text-[color:var(--maroon)]" />
                        Every cohort follows this pathway. Current cohort: {COHORT_INFO.cohortMonth}.
                    </div>
                </div>
            </div>
        </section>
    );
}
